import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Star, User, SlidersHorizontal, Lock, CalendarClock, Award, Plus, Trash2, Save, Wrench, Briefcase, Camera, X, Check, ChevronDown } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import PageHeader from "@/components/layout/PageHeader";
import { api, API, getToken, formatApiError } from "@/lib/api";
import { DIAL_CODES, GENDER_OPTIONS } from "@/lib/dialCodes";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { useTheme } from "@/contexts/ThemeContext";
import { toast } from "sonner";
import AvailabilityEditor from "@/pages/profile/AvailabilityEditor";
import SlotPreview from "@/pages/profile/SlotPreview";

const AVAILABILITY_ROLES = ["physician", "hah_physician", "nurse", "hah_nurse", "hah_technician", "technician", "hah_coordinator", "care_coordinator"];
const PERF_ROLES = ["physician", "hah_physician", "nurse", "hah_nurse"];
const SKILL_ROLES = ["physician", "hah_physician", "nurse", "hah_nurse", "hah_technician", "technician", "physiotherapist", "hah_physiotherapist"];
const PROFESSIONAL_ROLES = ["physician", "hah_physician", "nurse", "hah_nurse", "hah_technician", "technician", "physiotherapist", "hah_physiotherapist"];

function Field({ label, children, error }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-[#6B7280]">{label}</Label>
      {children}
      {error && <div className="text-xs text-[#EF4444]">{error}</div>}
    </div>
  );
}

// ---------------- Profile tab ----------------
function ProfileTab({ profile, onSaved }) {
  const [form, setForm] = useState({
    first_name: profile.first_name || "", second_name: profile.second_name || "", last_name: profile.last_name || "",
    gender: profile.gender || "", dob: profile.dob ? String(profile.dob).slice(0, 10) : "",
    nationality: profile.nationality || "", years_of_experience: profile.years_of_experience ?? "",
    dial_code: profile.dial_code || "", phone: profile.phone || "",
    alternate_dial_code: profile.alternate_dial_code || "", alternate_phone: profile.alternate_phone || "",
    title: profile.title || "", department: profile.department || "",
  });
  const [nationalities, setNationalities] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/forms/lookup-options/nationality").then((r) => setNationalities(r.data.items || [])).catch(() => {});
  }, []);

  // Live (on-the-spot) validation
  function computeErrors(f) {
    const e = {};
    if (!f.first_name.trim()) e.first_name = "First name is required";
    if (!f.last_name.trim()) e.last_name = "Last name is required";
    if (f.dob && f.dob > new Date().toISOString().slice(0, 10)) e.dob = "Date of birth cannot be in the future";
    if (f.years_of_experience !== "" && f.years_of_experience !== null && (!/^\d{1,2}$/.test(String(f.years_of_experience)) || Number(f.years_of_experience) > 70)) e.years_of_experience = "Enter a whole number between 0 and 70";
    if (f.phone && !f.dial_code) e.dial_code = "Select a dial code before entering the phone number";
    return e;
  }
  useEffect(() => { setErrors(computeErrors(form)); /* eslint-disable-next-line */ }, [form]);

  async function save() {
    const e = computeErrors(form);
    setErrors(e);
    if (Object.keys(e).length > 0) { toast.error("Please fix the highlighted fields before saving."); return; }
    setSaving(true);
    try {
      const payload = { ...form, years_of_experience: form.years_of_experience === "" ? null : Number(form.years_of_experience) };
      const res = await api.put("/profile/me", payload);
      toast.success("Profile updated");
      onSaved && onSaved(res.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  return (
    <div className="hah-card p-6 max-w-2xl me-auto" data-testid="profile-tab">
      <h3 className="text-base font-semibold text-[#1F2937] mb-4">Personal information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="First name" error={errors.first_name}><Input data-testid="profile-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
        <Field label="Second name"><Input data-testid="profile-second-name" value={form.second_name} onChange={(e) => setForm({ ...form, second_name: e.target.value })} placeholder="Optional" /></Field>
        <Field label="Last name" error={errors.last_name}><Input data-testid="profile-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Field>
        <Field label="Email (read-only)"><Input data-testid="profile-email" value={profile.email} disabled className="bg-[#F3F4F6] text-[#6B7280]" /></Field>
        <Field label="Gender">
          <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
            <SelectTrigger data-testid="profile-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
            <SelectContent>
              {GENDER_OPTIONS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Date of birth" error={errors.dob}><Input data-testid="profile-dob" type="date" max={new Date().toISOString().slice(0, 10)} value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></Field>
        <Field label="Nationality">
          <Select value={form.nationality} onValueChange={(v) => setForm({ ...form, nationality: v })}>
            <SelectTrigger data-testid="profile-nationality"><SelectValue placeholder="Select nationality" /></SelectTrigger>
            <SelectContent>
              {nationalities.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Years of experience" error={errors.years_of_experience}><Input data-testid="profile-years-experience" type="number" min="0" max="70" value={form.years_of_experience} onChange={(e) => setForm({ ...form, years_of_experience: e.target.value })} /></Field>
        <div className="md:col-span-2">
          <Field label="Phone" error={errors.dial_code}>
            <div className="flex gap-2">
              <Select value={form.dial_code} onValueChange={(v) => setForm({ ...form, dial_code: v })}>
                <SelectTrigger data-testid="profile-dial-code" className="w-[130px] shrink-0"><SelectValue placeholder="Code" /></SelectTrigger>
                <SelectContent>{DIAL_CODES.map((d) => <SelectItem key={d.code} value={d.code}>{d.code} · {d.country}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="profile-phone" className="flex-1" disabled={!form.dial_code} placeholder={!form.dial_code ? "Select dial code first" : "Phone number"} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Alternate contact number (optional)">
            <div className="flex gap-2">
              <Select value={form.alternate_dial_code} onValueChange={(v) => setForm({ ...form, alternate_dial_code: v })}>
                <SelectTrigger data-testid="profile-alt-dial-code" className="w-[130px] shrink-0"><SelectValue placeholder="Code" /></SelectTrigger>
                <SelectContent>{DIAL_CODES.map((d) => <SelectItem key={d.code} value={d.code}>{d.code} · {d.country}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="profile-alt-phone" className="flex-1" disabled={!form.alternate_dial_code} placeholder={!form.alternate_dial_code ? "Select dial code first" : "Alternate number"} value={form.alternate_phone} onChange={(e) => setForm({ ...form, alternate_phone: e.target.value })} />
            </div>
          </Field>
        </div>
        <Field label="Title"><Input data-testid="profile-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. MD, RN" /></Field>
        <Field label="Department"><Input data-testid="profile-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></Field>
        <Field label="Role (read-only)"><Input value={(profile.role_code || "").replace(/_/g, " ")} disabled className="bg-[#F3F4F6] text-[#6B7280] capitalize" /></Field>
      </div>
      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="profile-save">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

// ---------------- Preferences tab ----------------
function PreferencesTab({ profile }) {
  const { locale, setLocale } = useI18n();
  const { theme, setTheme } = useTheme();
  const [busy, setBusy] = useState(false);

  async function persist(next) {
    setBusy(true);
    try { await api.put("/profile/preferences", next); toast.success("Preference saved"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="hah-card p-6 max-w-2xl me-auto space-y-6" data-testid="preferences-tab">
      <div>
        <h3 className="text-base font-semibold text-[#1F2937] mb-1">Default language</h3>
        <p className="text-xs text-[#6B7280] mb-3">Applied across the platform whenever you sign in.</p>
        <Select value={locale} onValueChange={(v) => { setLocale(v); persist({ language: v }); }} disabled={busy}>
          <SelectTrigger className="w-56" data-testid="pref-language"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="ar">العربية (Arabic)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="border-t border-[#E5E7EB] pt-6">
        <h3 className="text-base font-semibold text-[#1F2937] mb-1">Theme</h3>
        <p className="text-xs text-[#6B7280] mb-3">Choose light or dark appearance.</p>
        <div className="flex gap-3">
          {["light", "dark"].map((th) => (
            <button
              key={th}
              data-testid={`pref-theme-${th}`}
              onClick={() => { setTheme(th); persist({ theme: th }); }}
              className={`px-5 py-3 rounded-lg border text-sm font-medium capitalize transition-colors ${
                theme === th ? "border-[#0B7E8C] ring-1 ring-[#0B7E8C] bg-[#0B7E8C]/5 text-[#0B7E8C]" : "border-[#E5E7EB] text-[#6B7280] hover:border-[#0B7E8C]/50"
              }`}
            >
              {th}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- Security tab ----------------
function SecurityTab() {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { toast.error("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/profile/change-password", { new_password: pw });
      toast.success("Password updated");
      setPw(""); setConfirm("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="hah-card p-6 max-w-md me-auto space-y-4" data-testid="security-tab">
      <div>
        <h3 className="text-base font-semibold text-[#1F2937]">Change password</h3>
        <p className="text-xs text-[#6B7280] mt-1">At least 8 characters, including a letter and a number.</p>
      </div>
      <Field label="New password"><Input type="password" data-testid="pw-new" value={pw} onChange={(e) => setPw(e.target.value)} /></Field>
      <Field label="Confirm new password"><Input type="password" data-testid="pw-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
      <Button onClick={save} disabled={busy} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white w-full" data-testid="pw-save">
        <Lock className="w-4 h-4 mr-1.5" /> {busy ? "Updating…" : "Update password"}
      </Button>
    </div>
  );
}

// ---------------- Performance tab ----------------
function Stars({ value, size = 16 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} width={size} height={size}
          className={i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-[#D1D5DB]"} />
      ))}
    </div>
  );
}

function PerformanceTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/performance/me").then((r) => setData(r.data)).catch(() => setData({ average: 0, count: 0, distribution: {}, reviews: [] })); }, []);
  if (!data) return <div className="text-sm text-[#6B7280]">Loading…</div>;
  const total = data.count || 0;
  return (
    <div className="space-y-4" data-testid="performance-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="hah-card p-6 flex flex-col items-center justify-center">
          <div className="text-5xl font-bold text-[#1F2937]" data-testid="perf-average">{data.average?.toFixed(2) ?? "0.00"}</div>
          <div className="mt-2"><Stars value={data.average} size={22} /></div>
          <div className="text-xs text-[#6B7280] mt-2">{total} review{total === 1 ? "" : "s"} · fed from the mobile app</div>
        </div>
        <div className="hah-card p-6 md:col-span-2">
          <h4 className="text-sm font-semibold text-[#1F2937] mb-3">Rating breakdown</h4>
          {[5, 4, 3, 2, 1].map((star) => {
            const c = data.distribution?.[String(star)] || 0;
            const pct = total ? Math.round((c / total) * 100) : 0;
            return (
              <div key={star} className="flex items-center gap-3 mb-2">
                <div className="flex items-center gap-1 w-10 text-xs text-[#6B7280]">{star}<Star className="w-3 h-3 fill-amber-400 text-amber-400" /></div>
                <div className="flex-1 h-2.5 rounded-full bg-[#F1F5F9] overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${pct}%` }} /></div>
                <div className="w-8 text-right text-xs text-[#6B7280]">{c}</div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="hah-card p-6">
        <h4 className="text-sm font-semibold text-[#1F2937] mb-3">Recent feedback</h4>
        {(data.reviews || []).length === 0 ? (
          <div className="text-sm text-[#6B7280]">No reviews yet.</div>
        ) : (
          <div className="space-y-3">
            {data.reviews.map((r, i) => (
              <div key={i} className="border border-[#E5E7EB] rounded-lg p-3" data-testid={`perf-review-${i}`}>
                <div className="flex items-center justify-between">
                  <Stars value={r.rating} />
                  <span className="text-xs text-[#94A3B8]">{(r.created_at || "").slice(0, 10)}</span>
                </div>
                {r.comment && <p className="text-sm text-[#374151] mt-1.5">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Professional tab (patient-facing details) ----------------
function PhotoAvatar({ profile, size = 96 }) {
  const src = useMemo(() => {
    if (!profile?.photo_url) return null;
    const token = getToken();
    const sep = profile.photo_url.includes("?") ? "&" : "?";
    // photo_url is like "/api/profile/photo/{id}?v=..." — strip leading /api since API already contains /api
    const path = profile.photo_url.startsWith("/api/") ? profile.photo_url.slice(4) : profile.photo_url;
    return `${API}${path}${sep}auth=${encodeURIComponent(token || "")}`;
  }, [profile?.photo_url]);
  const initials = ((profile?.first_name || "").charAt(0) + (profile?.last_name || "").charAt(0)).toUpperCase() || "?";
  return (
    <div
      className="rounded-full bg-[#0B7E8C]/10 text-[#0B7E8C] flex items-center justify-center overflow-hidden border-2 border-white shadow-sm"
      style={{ width: size, height: size, fontSize: size * 0.36, fontWeight: 700 }}
      data-testid="profile-photo-avatar"
    >
      {src ? <img src={src} alt="Profile" className="w-full h-full object-cover" /> : <span>{initials}</span>}
    </div>
  );
}

function ChipMulti({ selected, onChange, options = [], allowCustom = false, placeholder, testid, itemMaxLen = 120, maxItems = 20 }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectedLower = useMemo(() => new Set(selected.map((s) => s.toLowerCase())), [selected]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.filter((o) => !selectedLower.has(o.value.toLowerCase()));
    return options.filter((o) =>
      !selectedLower.has(o.value.toLowerCase()) &&
      ((o.label || "").toLowerCase().includes(q) || (o.value || "").toLowerCase().includes(q))
    );
  }, [options, query, selectedLower]);
  const canAddCustom = allowCustom && query.trim() && !selectedLower.has(query.trim().toLowerCase());

  function add(val) {
    const v = String(val || "").trim();
    if (!v) return;
    if (v.length > itemMaxLen) { toast.error(`Value too long (max ${itemMaxLen} chars)`); return; }
    if (selectedLower.has(v.toLowerCase())) return;
    if (selected.length >= maxItems) { toast.error(`Maximum ${maxItems} items allowed`); return; }
    onChange([...selected, v]);
    setQuery("");
  }
  function remove(val) { onChange(selected.filter((s) => s !== val)); }

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap gap-1.5 min-h-[36px] rounded-md border border-[#E5E7EB] bg-white px-2 py-1.5">
        {selected.length === 0 && <span className="text-xs text-[#9CA3AF] self-center px-1">{placeholder || "None selected"}</span>}
        {selected.map((s) => (
          <span key={s} className="inline-flex items-center gap-1 rounded-full bg-[#0B7E8C]/10 text-[#0B7E8C] border border-[#0B7E8C]/30 px-2 py-0.5 text-[11px] font-medium" data-testid={`${testid}-chip`}>
            <span className="truncate max-w-[220px]">{s}</span>
            <button type="button" onClick={() => remove(s)} className="hover:text-red-600 rounded p-0.5" aria-label={`Remove ${s}`} data-testid={`${testid}-remove`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0B7E8C] hover:text-[#128C97]" data-testid={`${testid}-add-btn`}>
            <Plus className="w-3.5 h-3.5" /> Add {allowCustom ? "or search" : "from list"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <div className="p-2 border-b border-slate-100">
            <input
              type="text"
              placeholder={allowCustom ? "Type to search or add custom…" : "Search…"}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && canAddCustom) { e.preventDefault(); add(query); } }}
              className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:outline-none focus:ring-1 focus:ring-[#0B7E8C]"
              data-testid={`${testid}-search`}
              autoFocus
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {canAddCustom && (
              <div
                onClick={() => add(query)}
                className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50 cursor-pointer text-sm border-b border-slate-100"
                data-testid={`${testid}-add-custom`}
              >
                <Plus className="w-4 h-4 text-[#0B7E8C]" />
                <span className="text-slate-900">Add “<span className="font-medium">{query.trim()}</span>”</span>
              </div>
            )}
            {filtered.length === 0 && !canAddCustom ? (
              <div className="px-3 py-6 text-center text-xs text-slate-500">
                {options.length === 0 ? "No options available." : query ? "No matches." : "Everything already selected."}
              </div>
            ) : (
              filtered.map((o) => (
                <div
                  key={o.value}
                  onClick={() => add(o.value)}
                  className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50 cursor-pointer text-sm"
                  data-testid={`${testid}-option-${o.value}`}
                >
                  <Check className="w-4 h-4 text-transparent" />
                  <span className="text-slate-900 flex-1 truncate">{o.label}</span>
                </div>
              ))
            )}
          </div>
          <div className="border-t border-slate-100 px-2.5 py-1.5 text-[11px] text-slate-500">
            {selected.length}/{maxItems} selected
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ProfessionalTab({ profile, onSaved }) {
  const [form, setForm] = useState({
    bio: profile.bio || "",
    certifications: Array.isArray(profile.certifications) ? profile.certifications : [],
    languages_spoken: Array.isArray(profile.languages_spoken) ? profile.languages_spoken : [],
  });
  const [certOptions, setCertOptions] = useState([]);
  const [langOptions, setLangOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoBust, setPhotoBust] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/profile/certifications-options").then((r) => setCertOptions(r.data.options || [])).catch(() => setCertOptions([]));
    api.get("/profile/language-options").then((r) => setLangOptions(r.data.options || [])).catch(() => setLangOptions([]));
  }, []);

  const bioLen = form.bio.length;
  const bioTooLong = bioLen > 1000;

  async function save() {
    if (bioTooLong) { toast.error("Bio must be 1000 characters or fewer."); return; }
    setSaving(true);
    try {
      const res = await api.put("/profile/me", {
        bio: form.bio.trim(),
        certifications: form.certifications,
        languages_spoken: form.languages_spoken,
      });
      toast.success("Professional profile updated");
      onSaved && onSaved(res.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  async function onPhotoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-picked
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) { toast.error("Photo must be PNG, JPG or WEBP."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo exceeds 5MB limit."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile photo updated");
      setPhotoBust(Date.now());
      onSaved && onSaved({ ...profile, photo_url: res.data.photo_url });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  }

  async function removePhoto() {
    setUploading(true);
    try {
      await api.delete("/profile/photo");
      toast.success("Profile photo removed");
      onSaved && onSaved({ ...profile, photo_url: null });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  }

  // Cache-busted profile snapshot for avatar rendering
  const bustedProfile = useMemo(
    () => (photoBust && profile.photo_url ? { ...profile, photo_url: `${profile.photo_url}${profile.photo_url.includes("?") ? "&" : "?"}b=${photoBust}` } : profile),
    [photoBust, profile]
  );

  return (
    <div className="hah-card p-6 max-w-3xl me-auto space-y-6" data-testid="professional-tab">
      <div>
        <h3 className="text-base font-semibold text-[#1F2937]">Patient-facing details</h3>
        <p className="text-xs text-[#6B7280] mt-1">
          These details are shown to patients when they browse clinicians and book teleconsultation sessions.
        </p>
      </div>

      {/* Photo */}
      <div className="border border-[#E5E7EB] rounded-lg p-4 flex items-start gap-4">
        <PhotoAvatar profile={bustedProfile} size={96} />
        <div className="flex-1">
          <div className="text-sm font-semibold text-[#1F2937]">Profile photo</div>
          <p className="text-xs text-[#6B7280] mt-1">
            Square headshot works best. PNG / JPG / WEBP up to 5MB.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onPhotoChange} data-testid="profile-photo-input" />
            <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white h-9" data-testid="profile-photo-upload-btn">
              <Camera className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : (profile.photo_url ? "Replace photo" : "Upload photo")}
            </Button>
            {profile.photo_url && (
              <Button type="button" variant="outline" onClick={removePhoto} disabled={uploading} className="h-9 border-[#E5E7EB] text-[#EF4444] hover:text-red-700 hover:bg-red-50" data-testid="profile-photo-remove-btn">
                <Trash2 className="w-4 h-4 mr-1.5" /> Remove
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Bio */}
      <Field label="About / Bio">
        <Textarea
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
          placeholder="Introduce yourself to patients — clinical focus, years of experience, treatment approach…"
          rows={5}
          maxLength={1200}
          className="resize-y"
          data-testid="professional-bio"
        />
        <div className={`text-[11px] mt-1 ${bioTooLong ? "text-[#EF4444]" : "text-[#9CA3AF]"}`} data-testid="professional-bio-count">
          {bioLen}/1000 characters
        </div>
      </Field>

      {/* Certifications */}
      <Field label="Certifications">
        <div className="text-[11px] text-[#6B7280] -mt-1 mb-2">
          Pick from the suggested list or type to add a custom certification.
        </div>
        <ChipMulti
          selected={form.certifications}
          onChange={(next) => setForm({ ...form, certifications: next })}
          options={certOptions}
          allowCustom
          placeholder="No certifications added"
          testid="professional-certifications"
          itemMaxLen={120}
          maxItems={20}
        />
      </Field>

      {/* Languages spoken */}
      <Field label="Languages spoken">
        <div className="text-[11px] text-[#6B7280] -mt-1 mb-2">
          Pick from the standard languages list. Ask an admin to add more via Lookups → Language.
        </div>
        <ChipMulti
          selected={form.languages_spoken}
          onChange={(next) => setForm({ ...form, languages_spoken: next })}
          options={langOptions}
          allowCustom={false}
          placeholder="No languages selected"
          testid="professional-languages"
          maxItems={10}
        />
      </Field>

      {/* Admin-managed specialties (read-only summary) */}
      {(profile.specialty_type || profile.medical_specialization || profile.sub_specialty) && (
        <div className="border-t border-[#E5E7EB] pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Specialty (admin)"><Input value={profile.specialty_type || "—"} disabled className="bg-[#F3F4F6] text-[#6B7280]" /></Field>
          <Field label="Medical specialty (admin)"><Input value={profile.medical_specialization || "—"} disabled className="bg-[#F3F4F6] text-[#6B7280]" /></Field>
          <Field label="Sub-specialty (admin)"><Input value={profile.sub_specialty || "—"} disabled className="bg-[#F3F4F6] text-[#6B7280]" /></Field>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving || bioTooLong} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="professional-save">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

// ---------------- Skills tab ----------------
function SkillsTab() {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/profile/skills");
      setOptions(r.data.options || []);
      setSelected(r.data.skills || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  function toggle(v) { setSelected((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]); }

  async function save() {
    if (selected.length === 0) { toast.error("Select at least one skill."); return; }
    setSaving(true);
    try {
      await api.put("/profile/skills", { skills: selected });
      toast.success("Skills updated — reflected in Fleet › Clinician Availability");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="text-sm text-[#6B7280]" data-testid="skills-loading">Loading…</div>;

  return (
    <div className="hah-card p-6 max-w-2xl me-auto" data-testid="skills-tab">
      <h3 className="text-base font-semibold text-[#1F2937] mb-1">My clinical skills</h3>
      <p className="text-xs text-[#6B7280] mb-4">
        Select the competencies you can perform. These appear on the Fleet › Clinician Availability tab and drive
        skill-matching for service assignment. <span className="font-medium">At least one skill is required.</span>
      </p>
      {options.length === 0 ? (
        <div className="text-sm text-[#6B7280]">No skills defined yet. Ask an admin to add options under Lookups → “Clinical Skills”.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {options.map((o) => {
            const checked = selected.includes(o.value);
            return (
              <label key={o.value} data-testid={`skill-opt-${o.value}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${checked ? "border-[#0B7E8C] bg-[#0B7E8C0A]" : "border-[#E5E7EB]"}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle(o.value)} className="rounded" />
                <span className="text-[#1F2937]">{o.label}</span>
              </label>
            );
          })}
        </div>
      )}
      <div className="mt-3 text-xs text-[#6B7280]" data-testid="skills-count">{selected.length} selected</div>
      <div className="mt-5 flex justify-end">
        <Button onClick={save} disabled={saving || selected.length === 0} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="skills-save">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save skills"}
        </Button>
      </div>
    </div>
  );
}

// ---------------- Main page ----------------
export default function MyProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const role = user?.role_code;
  const showAvailability = AVAILABILITY_ROLES.includes(role);
  const showPerformance = PERF_ROLES.includes(role);
  const showSkills = SKILL_ROLES.includes(role);
  const showProfessional = PROFESSIONAL_ROLES.includes(role);

  const load = useCallback(() => {
    api.get("/profile/me").then((r) => setProfile(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!profile) return <div className="text-sm text-[#6B7280]" data-testid="profile-loading">Loading…</div>;

  return (
    <div data-testid="my-profile-page">
      <PageHeader title="My Profile" subtitle="Manage your details, preferences, availability and reviews." />
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-1.5" /> Profile</TabsTrigger>
          {showProfessional && <TabsTrigger value="professional" data-testid="tab-professional"><Briefcase className="w-4 h-4 mr-1.5" /> Professional</TabsTrigger>}
          <TabsTrigger value="preferences" data-testid="tab-preferences"><SlidersHorizontal className="w-4 h-4 mr-1.5" /> Preferences</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security"><Lock className="w-4 h-4 mr-1.5" /> Security</TabsTrigger>
          {showAvailability && <TabsTrigger value="availability" data-testid="tab-availability"><CalendarClock className="w-4 h-4 mr-1.5" /> Availability</TabsTrigger>}
          {showSkills && <TabsTrigger value="skills" data-testid="tab-skills"><Wrench className="w-4 h-4 mr-1.5" /> Skills</TabsTrigger>}
          {showPerformance && <TabsTrigger value="performance" data-testid="tab-performance"><Award className="w-4 h-4 mr-1.5" /> Performance</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile"><ProfileTab profile={profile} onSaved={setProfile} /></TabsContent>
        {showProfessional && <TabsContent value="professional"><ProfessionalTab profile={profile} onSaved={setProfile} /></TabsContent>}
        <TabsContent value="preferences"><PreferencesTab profile={profile} /></TabsContent>
        <TabsContent value="security"><SecurityTab /></TabsContent>
        {showAvailability && (
          <TabsContent value="availability">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <AvailabilityEditor />
              <SlotPreview userId={profile.id} />
            </div>
          </TabsContent>
        )}
        {showPerformance && <TabsContent value="performance"><PerformanceTab /></TabsContent>}
        {showSkills && <TabsContent value="skills"><SkillsTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

export { Stars };
